import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Button, Spinner } from "../components/ui";
import { WorkStatusPill, ProgressBar, QuadrantBadge } from "../components/work";
import GoalFormModal from "../components/GoalFormModal";
import ProjectFormModal from "../components/ProjectFormModal";
import { getSpaceIcon } from "../lib/templates";
import { ArrowLeft, Plus, Target, FolderKanban } from "lucide-react";

function WorkRow({ item, color, t, lang, icon: Icon, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border bg-surface hover:bg-surface-hover transition-colors text-left">
      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{item.name}</span>
          <QuadrantBadge quadrant={item.quadrant} t={t} />
        </div>
        <div className="mt-1.5 max-w-xs"><ProgressBar value={item.progress} color={color} /></div>
      </div>
      <WorkStatusPill status={item.status} lang={lang} />
    </button>
  );
}

export default function SpacePage() {
  const { key, spaceId } = useParams();
  const navigate = useNavigate();
  const { t, lang, entities } = useApp();
  const entity = entities.find((e) => e.key === key);

  const [space, setSpace] = useState(null);
  const [goals, setGoals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGoal, setShowGoal] = useState(false);
  const [showProject, setShowProject] = useState(false);

  const load = useCallback(async () => {
    if (!entity) return;
    setLoading(true);
    try {
      const [sp, g, p, o, kr] = await Promise.all([
        api.get(`/spaces?entity_id=${entity.id}`),
        api.get(`/goals?space_id=${spaceId}`),
        api.get(`/space-projects?space_id=${spaceId}`),
        api.get("/data/objectives"),
        api.get("/data/key_results"),
      ]);
      setSpace(sp.data.find((s) => s.id === spaceId) || null);
      setGoals(g.data);
      setProjects(p.data);
      const objTitle = Object.fromEntries(o.data.map((x) => [x.id, x.title]));
      setKeyResults(kr.data.map((k) => ({ ...k, objective_title: objTitle[k.objective_id] })));
    } finally { setLoading(false); }
  }, [entity, spaceId]);

  useEffect(() => { load(); }, [load]);

  if (!entity) return <div className="p-8 text-text-secondary">…</div>;
  if (loading) return <div className="py-24 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;

  const color = entity.color;
  const Icon = getSpaceIcon(space?.icon);
  // top-level goals only (subgoals shown inside their parent)
  const topGoals = goals.filter((g) => !g.parent_goal_id);
  const topProjects = projects.filter((p) => !p.parent_goal_id);

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto" data-testid="space-page">
      <button onClick={() => navigate(`/entity/${key}`)} className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors" data-testid="space-back">
        <ArrowLeft className="w-4 h-4" /> {entity.name}
      </button>

      <div className="flex items-center gap-4 mb-8">
        <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}22` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </span>
        <div>
          <h1 className="text-3xl font-heading font-medium tracking-tight">{space?.name}</h1>
          {space?.description && <p className="text-text-secondary text-sm mt-1">{space.description}</p>}
        </div>
      </div>

      {/* Goals */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Target className="w-4 h-4 text-text-secondary" /><h3 className="font-heading font-medium">{t("goals")}</h3></div>
        <Button size="sm" variant="outline" onClick={() => setShowGoal(true)} data-testid="add-goal-button"><Plus className="w-3.5 h-3.5" /> {t("add_goal")}</Button>
      </div>
      <div className="space-y-2 mb-8">
        {topGoals.length === 0 ? <Card className="py-8 text-center text-text-secondary text-sm">{t("no_goals")}</Card> :
          topGoals.map((g) => <WorkRow key={g.id} item={g} color={color} t={t} lang={lang} icon={Target}
            testid={`goal-row-${g.id}`} onClick={() => navigate(`/entity/${key}/space/${spaceId}/goal/${g.id}`)} />)}
      </div>

      {/* Projects */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><FolderKanban className="w-4 h-4 text-text-secondary" /><h3 className="font-heading font-medium">{t("projects")}</h3></div>
        <Button size="sm" variant="outline" onClick={() => setShowProject(true)} data-testid="add-project-button"><Plus className="w-3.5 h-3.5" /> {t("add_project")}</Button>
      </div>
      <div className="space-y-2">
        {topProjects.length === 0 ? <Card className="py-8 text-center text-text-secondary text-sm">{t("no_projects")}</Card> :
          topProjects.map((p) => <WorkRow key={p.id} item={p} color={color} t={t} lang={lang} icon={FolderKanban}
            testid={`project-row-${p.id}`} onClick={() => navigate(`/entity/${key}/space/${spaceId}/project/${p.id}`)} />)}
      </div>

      <GoalFormModal open={showGoal} onClose={() => setShowGoal(false)} onSaved={load}
        entityId={entity.id} spaceId={spaceId} keyResults={keyResults} />
      <ProjectFormModal open={showProject} onClose={() => setShowProject(false)} onSaved={load}
        entityId={entity.id} spaceId={spaceId} />
    </div>
  );
}
